'use client'

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

import { Button } from "@/components/ui/button";
import { getImageUrl } from "@/shared/utils/image";

const heroImages = [
  "/convocation/convocation-image-2.jpg",
  "/convocation/convocation-image-6.jpg",
  "/convocation/convocation-image-11.jpg",
  "/convocation/convocation-image-17.jpg",
  "/convocation/convocation-image-21.jpg",
];

export const ConvocationHeroSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const activeImage = heroImages[currentIndex] ?? heroImages[0] ?? null;

  useEffect(() => {
    if (heroImages.length <= 1) {
      return undefined;
    }

    const interval = setInterval(() => {
      setCurrentIndex((previousIndex) => (previousIndex + 1) % heroImages.length);
    }, 6000);

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen w-full overflow-hidden bg-stone-950">
      {activeImage ? (
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            className="absolute inset-0"
          >
            <Image
              src={getImageUrl(activeImage)}
              alt={`Convocation hero image ${currentIndex + 1}`}
              fill
              priority={currentIndex === 0}
              className="object-cover object-center brightness-[0.72]"
              sizes="100vw"
            />
          </motion.div>
        </AnimatePresence>
      ) : null}

      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.14),transparent_30%),linear-gradient(120deg,rgba(17,24,39,0.88),rgba(28,25,23,0.56)_42%,rgba(17,24,39,0.86)_100%)]" />
      <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-stone-950 via-stone-950/70 to-transparent" />

      <div className="relative z-10 mx-auto flex min-h-screen max-w-7xl items-center px-6 pt-28 pb-12 lg:px-12 lg:pt-36 lg:pb-16">
        <div className="max-w-4xl">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="text-[11px] uppercase tracking-[0.42em] text-stone-200/80"
          >
            Convocation Photography
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.85, delay: 0.4 }}
            className="mt-6 font-serif text-5xl leading-[1.02] tracking-[-0.04em] text-white sm:text-6xl lg:text-8xl"
          >
            Celebrate graduation
            <span className="block text-stone-200">with clean, timeless portraits.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 0.55 }}
            className="mt-5 max-w-3xl text-sm uppercase tracking-[0.3em] text-stone-200/78 sm:text-base"
          >
            Solo | Friends | Family
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 0.7 }}
            className="mt-6 max-w-xl text-base leading-8 text-stone-200/78 sm:text-lg"
          >
            Professional studio photography for one of your biggest milestones.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 0.85 }}
            className="mt-8"
          >
            <Button
              asChild
              size="lg"
              className="h-14 rounded-full bg-white px-8 text-xs uppercase tracking-[0.28em] text-stone-950 hover:bg-stone-100"
            >
              <Link href="/convocation/booking">Book Now</Link>
            </Button>
          </motion.div>
        </div>
      </div>

      {heroImages.length > 1 ? (
        <div className="absolute right-6 bottom-8 z-10 flex gap-3 sm:right-10 sm:bottom-10 lg:right-12 lg:bottom-12">
          {heroImages.map((image, index) => (
            <button
              key={image}
              type="button"
              onClick={() => setCurrentIndex(index)}
              className="group relative h-1 w-12 overflow-hidden rounded-full bg-white/20 transition-all"
              aria-label={`Show hero image ${index + 1}`}
            >
              <motion.div
                className="absolute inset-0 bg-white"
                initial={false}
                animate={{
                  x: index === currentIndex ? "0%" : "-100%",
                }}
                transition={{ duration: 0.5 }}
              />
            </button>
          ))}
        </div>
      ) : null}
    </section>
  );
};
